import type {
  TicketingAttachment,
  TicketingDetailsData,
  UploadedTicketingFile,
  UploadTicketingFileResponse
} from 'common/ticketing-details/types';

import {Button, Loading, TextArea} from '@ds/components';
import {DetailPageLayout} from '@ds/layout';
import {toast} from '@Toast';
import AttachmentItem from 'common/ticketing-details/AttachmentItem';
import AttachmentUploader from 'common/ticketing-details/AttachmentUploader';
import TicketingInfo from 'common/ticketing-details/TicketingInfo';
import TicketingMessage from 'common/ticketing-details/TicketingMessage';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  addTrackingComment,
  downloadAttachment,
  getTracking,
  uploadFiles
} from 'store/slices/tracking/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

type TrackingLocationState = {
  orderCode?: string | null;
};

const readOrderCode = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const TrackingDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const {id: trackingId} = useParams<{id: string}>();
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const tracking = useAppSelector((state) => state.tracking.tracking) as TicketingDetailsData;
  const listedOrderCode = useAppSelector((state) => {
    const items = state.tracking.trackings?.tickets;
    if (!Array.isArray(items) || !trackingId) {
      return null;
    }
    const match = items.find(
      (item: {id?: string | number; order_code?: string | null}) =>
        String(item.id) === String(trackingId)
    );
    return readOrderCode(match?.order_code);
  });
  const loading = useAppSelector((state) => state.tracking.loading) as number;
  const navigationOrderCode = readOrderCode(
    (location.state as TrackingLocationState | null)?.orderCode
  );
  const orderCode = readOrderCode(tracking?.order_code) ?? navigationOrderCode ?? listedOrderCode;
  const [files, setFiles] = useState<UploadedTicketingFile[]>([]);
  const [description, setDescription] = useState('');

  const loadTracking = useCallback(() => {
    if (!trackingId) {
      return;
    }
    void dispatch(getTracking({tracking_id: trackingId, vendor_id: vendorId}));
  }, [dispatch, trackingId, vendorId]);

  useEffect(() => {
    loadTracking();
  }, [loadTracking]);

  const handleUploadSuccess = (response: UploadTicketingFileResponse) => {
    setFiles((currentFiles) => [...currentFiles, response.data]);
  };

  const handleRemoveFile = (file: TicketingAttachment) => {
    if (!file.file_key) {
      return;
    }
    setFiles((currentFiles) =>
      currentFiles.filter((currentFile) => currentFile.file_key !== file.file_key)
    );
  };

  const addDescription = async () => {
    if (!trackingId) {
      return;
    }
    if (description.trim().length <= 10) {
      toast('توضیحات باید بیشتر از ۱۰ حرف باشد.');
      return;
    }

    await dispatch(
      addTrackingComment({
        tracking_id: trackingId,
        vendor_id: vendorId,
        description: description.trim(),
        file_ids: files.length > 0 ? files.map(({file_key}) => file_key) : null
      })
    ).unwrap();

    loadTracking();
    toast('توضیحات با موفقیت ثبت شد');
    setDescription('');
    setFiles([]);
  };

  const comments = useMemo(
    () => (Array.isArray(tracking?.comments) ? tracking.comments : []),
    [tracking?.comments]
  );
  const trackingAttachments = Array.isArray(tracking?.attachments) ? tracking.attachments : [];
  const submitting = loading > 0;

  if (submitting && !tracking?.id) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  return (
    <DetailPageLayout
      footer={
        <Button
          disabled={description.trim().length <= 10}
          loading={submitting}
          onClick={addDescription}
          fullWidth
        >
          افزودن توضیحات
        </Button>
      }
    >
      <TicketingInfo data={{...(tracking ?? {}), order_code: orderCode}} />

      <div className="h-2 shrink-0 bg-gray-02" aria-hidden />

      <section className="flex flex-col gap-2.5 p-2.5">
        <h2 className="py-2.5 text-body-md-emphasis text-neutral-fg">توضیحات اسنپ</h2>
        <TicketingMessage
          description={tracking?.description}
          attachments={trackingAttachments}
          vendorId={vendorId}
          downloadAttachment={downloadAttachment}
          source="snapp"
        />
      </section>

      {comments.length > 0 ? (
        <section className="flex flex-col gap-2.5 p-2.5" aria-label="پاسخ‌های پیگیری">
          {comments.map((comment) => (
            <TicketingMessage
              key={comment.id}
              title={comment.is_zf ? 'پاسخ اسنپ' : 'پاسخ شما'}
              description={comment.description}
              createdAt={comment.created_at}
              attachments={comment.attachments}
              vendorId={vendorId}
              downloadAttachment={downloadAttachment}
              source={comment.is_zf ? 'snapp' : 'vendor'}
            />
          ))}
        </section>
      ) : null}

      <div className="h-2 shrink-0 bg-gray-02" aria-hidden />

      <section className="flex flex-col gap-2.5 p-2.5">
        <h2 className="py-2.5 text-body-md-emphasis text-neutral-fg">
          نیاز به توضیح دارید؟ اینجا بنویسید
        </h2>
        <TextArea
          value={description}
          rows={6}
          placeholder="توضیح جزئیات مشکل"
          help="توضیحات باید بیشتر از ۱۰ حرف باشد."
          status={description.length > 0 && description.trim().length <= 10 ? 'error' : undefined}
          onChange={(event) => setDescription(event.target.value)}
        />
        <AttachmentUploader
          vendorId={vendorId}
          uploadAttachment={uploadFiles}
          onUploadSuccess={handleUploadSuccess}
        />

        {files.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {files.map((file) => (
              <AttachmentItem
                key={file.file_key}
                file={file}
                vendorId={vendorId}
                downloadAttachment={downloadAttachment}
                removable
                onRemove={handleRemoveFile}
              />
            ))}
          </div>
        ) : null}
      </section>
    </DetailPageLayout>
  );
};

export default TrackingDetails;
